import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { apiPost, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

interface FieldErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
  height?: string;
  general?: string;
}

export function SetupScreen(): ReactElement {
  const { refresh } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [height, setHeight] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    if (username.length < 3) errs.username = 'Username must be at least 3 characters';
    if (password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (password !== confirm) errs.confirmPassword = 'Passwords do not match';
    const h = Number(height);
    if (!Number.isFinite(h) || h < 50 || h > 250) {
      errs.height = 'Height must be a number between 50 and 250';
    }
    return errs;
  }

  async function onSubmit(ev: FormEvent<HTMLFormElement>): Promise<void> {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await apiPost<{ id: string; username: string; height_cm: number }>(
        '/api/auth/setup',
        {
          username,
          password,
          height_cm: Number(height),
        },
      );
      await refresh();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Setup failed';
      setErrors({ general: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="flex min-h-dvh items-center justify-center p-6"
      style={{ background: 'var(--bg-primary)', color: 'var(--text-main)' }}
      data-testid="setup-screen"
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border p-6 shadow-sm"
        style={{
          background: 'var(--bg-surface)',
          borderColor: 'var(--border)',
        }}
      >
        <header className="flex flex-col items-center gap-2 text-center">
          <div className="text-5xl" aria-hidden="true">👨‍🍳</div>
          <h1 className="text-2xl font-semibold">ChefCook Setup</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Welcome! Set up your administrator account to secure the application.
          </p>
        </header>

        <label className="flex flex-col gap-1 text-sm">
          <span>Username</span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            minLength={3}
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.username ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.username}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.password ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.password}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span>Confirm Password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.confirmPassword ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.confirmPassword}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span>Height in cm</span>
          <input
            type="number"
            inputMode="numeric"
            min={50}
            max={250}
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            required
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.height ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.height}
            </span>
          ) : null}
        </label>

        {errors.general ? (
          <p className="text-sm" style={{ color: '#b91c1c' }}>
            {errors.general}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="min-h-[48px] rounded-md font-semibold disabled:opacity-60"
          style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
        >
          {submitting ? 'Setting up…' : 'Create Administrator'}
        </button>
      </form>
    </main>
  );
}
